/**
 * Wave A — `useActiveTenant()`.
 *
 * Persists the active tenant id in `localStorage['janus.activeTenant']` and
 * exposes a `switch(id)` action that invalidates the `usePermissions()` cache
 * so subsequent requests carry the new `X-Active-Tenant` header.
 *
 * @module shared/hooks/useActiveTenant
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ME_PERMISSIONS_QUERY_KEY } from './usePermissions';
import { getOrganizationsClient } from '../../api/wave-a/client';

const STORAGE_KEY = 'janus.activeTenant';
const SWITCH_EVENT = 'janus:active-tenant-changed';

function readStored(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export interface UseActiveTenantResult {
  /** Currently active tenant id, or `null` if none selected. */
  tenantId: string | null;
  /**
   * Persists `id` and invalidates the `usePermissions()` query so the new
   * tenant scope is fetched with the proper `X-Active-Tenant` header.
   */
  switch: (id: string) => void;
}

export function useActiveTenant(): UseActiveTenantResult {
  const [tenantId, setTenantId] = useState<string | null>(() => readStored());
  const queryClient = useQueryClient();

  // Cross-tab + cross-hook sync.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === STORAGE_KEY) setTenantId(ev.newValue);
    };
    const onLocal = () => setTenantId(readStored());
    window.addEventListener('storage', onStorage);
    window.addEventListener(SWITCH_EVENT, onLocal as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SWITCH_EVENT, onLocal as EventListener);
    };
  }, []);

  const switchTenant = useCallback(
    (id: string) => {
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(STORAGE_KEY, id);
        } catch {
          // ignore quota errors
        }
        window.dispatchEvent(new Event(SWITCH_EVENT));
      }
      setTenantId(id);
      // Invalidate permission cache + clear in-process client cache so the
      // very next request refetches with the new X-Active-Tenant header.
      try {
        getOrganizationsClient().clearMyPermissionsCache();
      } catch {
        // ignore
      }
      queryClient.invalidateQueries({ queryKey: ME_PERMISSIONS_QUERY_KEY });
    },
    [queryClient],
  );

  return { tenantId, switch: switchTenant };
}
